import { useTranslation } from "react-i18next";

export type InspireView = "all" | "saved";

export type InspireScope = "full_home" | "partial";

const SCOPE_OPTIONS: readonly (InspireScope | null)[] = [
	null,
	"full_home",
	"partial",
];

interface InspireFilterBarProps {
	view: InspireView;
	onViewChange: (view: InspireView) => void;
	/**
	 * Room names across every public project the other filters leave (the
	 * server's `facets.rooms`), with the chosen room added when it is not among
	 * them, so the selected room stays visible as a chip while the other filters
	 * exclude it.
	 */
	rooms: readonly string[];
	activeRoom: string;
	onRoomChange: (room: string) => void;
	activeScope: InspireScope | null;
	onScopeChange: (scope: InspireScope | null) => void;
}

const baseChip =
	"relative h-9 shrink-0 whitespace-nowrap rounded-full px-[18px] transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]";

const activeChip = `${baseChip} bg-[linear-gradient(rgba(255,255,255,0.18)_0%,rgba(255,255,255,0)_45%,rgba(0,0,0,0.06)_100%),linear-gradient(var(--color-dashboard-accent-light)_0%,var(--color-dashboard-accent)_60%,var(--color-dashboard-accent-hover)_100%)] font-display text-[15px] font-medium italic tracking-[-0.005em] text-dashboard-on-fill shadow-[inset_0_1px_0_var(--color-dashboard-warm-400)/45,inset_0_-1px_0_var(--color-dashboard-ink)/15,0_1px_2px_var(--color-dashboard-ink)/10,0_6px_18px_-6px_var(--color-dashboard-accent)/45,0_12px_28px_-10px_var(--color-dashboard-accent)/35]`;

const chip = `${baseChip} bg-dashboard-bg/55 font-body text-[13px] font-medium tracking-[0.005em] border border-dashboard-warm-300/60 text-dashboard-muted-dark shadow-[inset_0_0_0_1px_var(--color-dashboard-warm-300)/60,0_1px_2px_var(--color-dashboard-ink)/4] hover:bg-dashboard-paper/70`;

export function InspireFilterBar({
	view,
	onViewChange,
	rooms,
	activeRoom,
	onRoomChange,
	activeScope,
	onScopeChange,
}: InspireFilterBarProps) {
	const { t } = useTranslation();
	const showFilters = view === "all";

	return (
		<div className="flex w-full items-center gap-4 mt-3 rounded-full border border-dashboard-border px-4 py-2 backdrop-blur-md dashboard-search-bar">
			<div className="flex shrink-0 gap-2">
				{(["all", "saved"] as const).map((option) => (
					<button
						key={option}
						type="button"
						aria-pressed={view === option}
						onClick={() => onViewChange(option)}
						className={view === option ? activeChip : chip}
					>
						{t(`inspire.view.${option}`)}
					</button>
				))}
			</div>

			{showFilters && (
				<>
					<div className="h-6 w-px shrink-0 bg-dashboard-warm-300/70" />

					<div className="flex shrink-0 gap-2">
						{SCOPE_OPTIONS.map((scope) => (
							<button
								key={scope ?? "all"}
								type="button"
								aria-pressed={activeScope === scope}
								onClick={() => onScopeChange(scope)}
								className={activeScope === scope ? activeChip : chip}
							>
								{t(`inspire.scope.${scope ?? "all"}`)}
							</button>
						))}
					</div>

					<div className="h-6 w-px shrink-0 bg-dashboard-warm-300/70" />

					<div className="scrollbar-hide flex flex-1 min-w-0 gap-2 overflow-x-auto">
						{["All", ...rooms].map((room) => (
							<button
								key={room}
								type="button"
								aria-pressed={activeRoom === room}
								onClick={() => onRoomChange(room)}
								className={activeRoom === room ? activeChip : chip}
							>
								{room}
							</button>
						))}
					</div>
				</>
			)}
		</div>
	);
}
